import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../../Constant';
import { _axios } from '../../../utils/axios';
import { ENTITY_TYPES } from './DonationManagementData';

const MAX_ITEMS = 10;
const MIN_CHAR_LEN = 5;
const TITLE_MAX = 50;
const DESC_MAX = 100;
const TITLE_FIELDS = ['title_en', 'title_hi', 'title_mr', 'title_gu'];
const DESC_FIELDS = ['description_en', 'description_hi', 'description_mr', 'description_gu'];
const emptyItem = () => ({
  title_en: '', title_hi: '', title_mr: '', title_gu: '',
  description_en: '', description_hi: '', description_mr: '', description_gu: '',
});

const CreateDonationManagement = ({ show, onHide }) => {
  const [entityType, setEntityType] = useState('');
  const [entitySearchInput, setEntitySearchInput] = useState('');
  const [entitySearchResults, setEntitySearchResults] = useState([]);
  const [isSearchingEntities, setIsSearchingEntities] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const [items, setItems] = useState([emptyItem()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActive, setIsActive] = useState(0);
  const searchTimerRef = useRef(null);

  // Fee config
  const [globalFeeType, setGlobalFeeType] = useState('PERCENTAGE');
  const [globalDefaultPercentage, setGlobalDefaultPercentage] = useState(null);
  const [feeType, setFeeType] = useState('PERCENTAGE');
  const [percentageMode, setPercentageMode] = useState('DEFAULT'); // DEFAULT | CUSTOM
  const [customPercentage, setCustomPercentage] = useState('');

  useEffect(() => {
    if (!show) {
      setEntityType('');
      setEntitySearchInput('');
      setEntitySearchResults([]);
      setSelectedEntity(null);
      setItems([emptyItem()]);
      setIsSubmitting(false);
      setIsActive(0);
      setFeeType('PERCENTAGE');
      setPercentageMode('DEFAULT');
      setCustomPercentage('');
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;
    (async () => {
      try {
        const res = await _axios.get(`${ServerPath}/convenience-fee/get`);
        if (res.data?.success === 1 && res.data?.data?.length > 0) {
          const cfg = res.data.data[0];
          setGlobalFeeType(cfg.fee_type || 'PERCENTAGE');
          setGlobalDefaultPercentage(cfg.default_percentage);
        }
      } catch (err) {
        console.error('Error fetching global fee config:', err);
      }
    })();
  }, [show]);

  const fetchEntityResults = (type, query) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setIsSearchingEntities(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await _axios.get(`${ServerPath}/admin-notification/favourite-entities`, {
          params: { type, search: query || '', limit: 20 }
        });
        if (data.success === 1 || data.success === true) setEntitySearchResults(data.data || []);
        else setEntitySearchResults([]);
      } catch (err) {
        console.error('Entity search error:', err);
        setEntitySearchResults([]);
      } finally {
        setIsSearchingEntities(false);
      }
    }, 300);
  };

  const handleEntityTypeChange = (e) => {
    setEntityType(e.target.value);
    setEntitySearchInput('');
    setEntitySearchResults([]);
    setSelectedEntity(null);
  };

  const handleEntitySearchChange = (e) => {
    const value = e.target.value;
    setEntitySearchInput(value);
    if (!entityType) return;
    fetchEntityResults(entityType, value);
  };

  const handleEntitySelect = (entity) => {
    if (!entityType) return;
    setSelectedEntity({
      object_type: entityType,
      object_id: entity.entity_id,
      object_name: entity.name,
      object_location: null,
    });
    setEntitySearchInput('');
    setEntitySearchResults([]);
  };

  const handleEntityRemove = () => setSelectedEntity(null);

  const handleItemChange = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };
  const handleAddItem = () => {
    if (items.length >= MAX_ITEMS) {
      toast.error(`Maximum ${MAX_ITEMS} donation items allowed`);
      return;
    }
    setItems(prev => [...prev, emptyItem()]);
  };
  const handleRemoveItem = (idx) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!selectedEntity) return toast.error('Please select an entity');
    if (items.length > MAX_ITEMS) return toast.error(`Maximum ${MAX_ITEMS} donation items allowed`);

    // Drop rows that are completely empty — they aren't validated and aren't posted.
    const isRowEmpty = (it) => Object.values(it).every(v => !v || !String(v).trim());
    const nonEmptyItems = items.filter(it => !isRowEmpty(it));

    // For non-empty rows, English title is required + min-length on any filled title/description.
    for (let i = 0; i < nonEmptyItems.length; i++) {
      const it = nonEmptyItems[i];
      if (!it.title_en || !it.title_en.trim()) {
        return toast.error(`Item #${i + 1}: English title is required`);
      }
      for (const k of TITLE_FIELDS) {
        const v = (it[k] || '').trim();
        if (v && v.length < MIN_CHAR_LEN) {
          return toast.error(`Item #${i + 1}: title must be at least ${MIN_CHAR_LEN} characters`);
        }
      }
      for (const k of DESC_FIELDS) {
        const v = (it[k] || '').trim();
        if (v && v.length < MIN_CHAR_LEN) {
          return toast.error(`Item #${i + 1}: description must be at least ${MIN_CHAR_LEN} characters`);
        }
      }
    }

    if (feeType === 'PERCENTAGE' && percentageMode === 'CUSTOM') {
      const pct = parseFloat(customPercentage);
      if (Number.isNaN(pct) || pct <= 0 || pct > 100) {
        return toast.error('Custom percentage must be between 0 and 100');
      }
    }

    let resolvedFeeType = feeType;
    let resolvedCustomPct = null;
    if (feeType === 'PERCENTAGE' && percentageMode === 'CUSTOM') {
      resolvedCustomPct = parseFloat(customPercentage);
    }

    setIsSubmitting(true);
    try {
      const objRes = await _axios.post(`${ServerPath}/master-donation-objects`, {
        object_type: selectedEntity.object_type,
        object_id: selectedEntity.object_id,
        object_name: selectedEntity.object_name,
        object_location: selectedEntity.object_location,
        is_active: isActive,
        fee_type: resolvedFeeType,
        custom_percentage: resolvedCustomPct,
      });
      if (!objRes.data?.success) {
        toast.error('Failed to create donation object');
        setIsSubmitting(false);
        return;
      }
      const donationObjectId = objRes.data.data?.donation_object_id;

      // Only POST items that have a title — silently drop empty placeholder rows.
      const itemsToCreate = nonEmptyItems.filter(it => it.title_en && it.title_en.trim());
      const failures = [];
      for (let i = 0; i < itemsToCreate.length; i++) {
        const it = itemsToCreate[i];
        const payload = {
          title_en: it.title_en.trim(),
          title_hi: it.title_hi?.trim() || null,
          title_mr: it.title_mr?.trim() || null,
          title_gu: it.title_gu?.trim() || null,
          description_en: it.description_en?.trim() || null,
          description_hi: it.description_hi?.trim() || null,
          description_mr: it.description_mr?.trim() || null,
          description_gu: it.description_gu?.trim() || null,
          sort_order: i + 1,
        };
        try {
          const itemRes = await _axios.post(`${ServerPath}/donation-items/object/${donationObjectId}`, payload);
          if (!itemRes.data?.success) failures.push(i + 1);
        } catch (err) {
          console.error('Item create error:', err);
          failures.push(i + 1);
        }
      }

      if (failures.length === 0) {
        toast.success('Donation created successfully');
        onHide(true);
      } else {
        toast.warn(`Donation created, but item(s) failed: ${failures.join(', ')}. You can retry from Edit.`);
        onHide(true);
      }
    } catch (err) {
      console.error('Create donation error:', err);
      toast.error('Failed to create donation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold">Create Donation</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">

        {/* Select Entity */}
        <Form.Label className="fw-semibold mb-2">Select Entity <span className="text-danger">*</span></Form.Label>
        <Row className="g-3 mb-4 align-items-start">
          <Col md={3}>
            <Form.Label className="small mb-1">Entity Type</Form.Label>
            <Form.Select value={entityType} onChange={handleEntityTypeChange} disabled={!!selectedEntity}>
              <option value="" disabled hidden>Select Entity Type</option>
              {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="small mb-1">Search Entity</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder={selectedEntity ? 'Entity selected — remove to change' : 'Search entity...'}
                value={entitySearchInput}
                onChange={handleEntitySearchChange}
                disabled={!entityType || !!selectedEntity}
              />
              {!selectedEntity && entityType && entitySearchInput.length > 0 && (
                <div
                  className="position-absolute w-100 bg-white border rounded mt-1 shadow-sm"
                  style={{ zIndex: 1000, maxHeight: 200, overflowY: 'auto' }}
                >
                  {isSearchingEntities ? (
                    <div className="p-2 text-muted small text-center">Searching...</div>
                  ) : entitySearchResults.length === 0 ? (
                    <div className="p-2 text-muted small text-center">No Result Found</div>
                  ) : (
                    entitySearchResults.map(e => (
                      <div
                        key={e.entity_id}
                        className="p-2"
                        style={{ cursor: 'pointer', fontSize: 14 }}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => handleEntitySelect(e)}
                      >
                        {e.name} 
                        {/* <span className="text-muted">({e.favourite_user_count ?? 0})</span> */}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </Col>
          <Col md={4}>
            <Form.Label className="small mb-1">Selected Entity</Form.Label>
            <div
              style={{
                minHeight: 38, border: '1px solid #E2E8F0', borderRadius: 6,
                padding: '6px 10px', background: '#fff',
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6
              }}
            >
              {!selectedEntity ? (
                <span className="text-muted small">No entity selected</span>
              ) : (
                <span
                  className="d-inline-flex align-items-center"
                  style={{ background: '#6366F1', color: 'white', padding: '4px 10px', borderRadius: 6, fontSize: 13 }}
                >
                  {selectedEntity.object_name}
                  <button
                    type="button"
                    onClick={handleEntityRemove}
                    className="ms-2 border-0 bg-transparent text-white"
                    style={{ padding: 0, lineHeight: 1, fontSize: 16, fontWeight: 'bold', cursor: 'pointer' }}
                  >×</button>
                </span>
              )}
            </div>
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Status</Form.Label>
            <Form.Select
              value={isActive ? 'ACTIVE' : 'ARCHIVED'}
              onChange={(e) => setIsActive(e.target.value === 'ACTIVE' ? 1 : 0)}
            >
              <option value="ARCHIVED">Inactive</option>
              <option value="ACTIVE">Active</option>
            </Form.Select>
          </Col>
        </Row>

        {/* Fee Type */}
        <Form.Label className="fw-semibold mb-2">Fee Type <span className="text-danger">*</span></Form.Label>
        <div className="border rounded p-3 mb-4" style={{ background: '#fafafa' }}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Check
                type="radio"
                label="Percentage"
                name="create-fee-type"
                checked={feeType === 'PERCENTAGE'}
                onChange={() => setFeeType('PERCENTAGE')}
              />
              <Form.Check
                type="radio"
                label="Fixed Amount Slab"
                name="create-fee-type"
                checked={feeType === 'FIXED_SLAB'}
                onChange={() => setFeeType('FIXED_SLAB')}
              />
            </Col>
            <Col md={8}>
              {feeType === 'PERCENTAGE' ? (
                <div className="border rounded p-2 bg-white">
                  <Form.Check
                    type="radio"
                    name="create-pct-mode"
                    checked={percentageMode === 'DEFAULT'}
                    onChange={() => setPercentageMode('DEFAULT')}
                    label={
                      <span>
                        Default
                        {globalDefaultPercentage != null && globalFeeType === 'PERCENTAGE' && (
                          <span className="text-muted"> ({globalDefaultPercentage}%)</span>
                        )}
                      </span>
                    }
                  />
                  <div className="d-flex align-items-center gap-2 mt-1">
                    <Form.Check
                      type="radio"
                      name="create-pct-mode"
                      checked={percentageMode === 'CUSTOM'}
                      onChange={() => setPercentageMode('CUSTOM')}
                      label="Custom"
                    />
                    {percentageMode === 'CUSTOM' && (
                      <div className="d-flex align-items-center gap-2 ms-3">
                        <span className="small text-muted">
                          {selectedEntity?.object_name || 'Selected entity'}
                        </span>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          max="100"
                          step="0.01"
                          style={{ width: 100 }}
                          value={customPercentage}
                          onChange={(e) => setCustomPercentage(e.target.value)}
                          placeholder="%"
                        />
                        <span>%</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border rounded p-2 bg-white text-muted small">
                  Global slab configuration will be applied to this entity.
                </div>
              )}
            </Col>
          </Row>
        </div>

        {/* Donation Items */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Label className="fw-semibold mb-0">Donation Items <span className="text-danger">*</span> <span className="text-muted small">({items.length}/{MAX_ITEMS})</span></Form.Label>
        </div>

        {items.map((item, idx) => (
          <div key={idx} className="border rounded p-3 mb-3" style={{ background: '#fafafa' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="small text-muted">Item #{idx + 1}</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleRemoveItem(idx)}
                disabled={items.length === 1}
              >×</button>
            </div>
            <Row className="g-3">
              {[
                { label: 'Eng', key: 'title_en', required: true },
                { label: 'Hin', key: 'title_hi' },
                { label: 'Mar', key: 'title_mr' },
                { label: 'Guj', key: 'title_gu' },
              ].map(f => (
                <Col md={3} key={f.key}>
                  <Form.Label className="small mb-1">
                    Donation Title({f.label}){f.required && <span className="text-danger"> *</span>}
                  </Form.Label>
                  <Form.Control
                    value={item[f.key]}
                    onChange={(e) => handleItemChange(idx, f.key, e.target.value)}
                    maxLength={TITLE_MAX}
                    placeholder={f.label}
                    style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                  />
                  <div className="text-end small text-muted">{(item[f.key] || '').length}/{TITLE_MAX}</div>
                </Col>
              ))}
            </Row>
            <Row className="g-3 mt-1">
              {[
                { label: 'Eng', key: 'description_en' },
                { label: 'Hin', key: 'description_hi' },
                { label: 'Mar', key: 'description_mr' },
                { label: 'Guj', key: 'description_gu' },
              ].map(f => (
                <Col md={3} key={f.key}>
                  <Form.Label className="small mb-1">Donation Description({f.label})</Form.Label>
                  <Form.Control
                    as="textarea" rows={3}
                    value={item[f.key]}
                    onChange={(e) => handleItemChange(idx, f.key, e.target.value)}
                    maxLength={DESC_MAX}
                    placeholder={f.label}
                    style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', resize: 'vertical' }}
                  />
                  <div className="text-end small text-muted">{(item[f.key] || '').length}/{DESC_MAX}</div>
                </Col>
              ))}
            </Row>
          </div>
        ))}

        <Button
          variant="primary"
          size="sm"
          onClick={handleAddItem}
          disabled={items.length >= MAX_ITEMS}
        >
          + Add Donation Item
        </Button>

      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 pb-4">
        <Button
          className="px-5 py-2"
          style={{ backgroundColor: '#6c757d', border: 'none', borderRadius: '8px' }}
          onClick={() => onHide(false)}
          disabled={isSubmitting}
        >Cancel</Button>
        <Button
          className="px-5 py-2"
          style={{ backgroundColor: '#5662F6', border: 'none', borderRadius: '8px' }}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >{isSubmitting ? 'Creating...' : 'Create Donation'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateDonationManagement;
